/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import { defineStore } from 'pinia'
import { ref, toRaw, watch } from 'vue'
import { getCurrentUser } from '@/io.ox/core/api/user'
import snippetsAPI from '@/io.ox/core/api/snippets'
import http from '@/io.ox/core/http'
import mailAPI from '@/io.ox/mail/api'
import { settings as coreSettings } from '@/io.ox/core/settings'
import { settings as mailSettings } from '@/io.ox/mail/settings'
import postalAddress from '@/io.ox/core/locale/postal-address'
import { getFontFormats } from '@/io.ox/mail/util'

import ox from '@/ox'
import gt from 'gettext'

export const useSignatureDesignerStore = defineStore('signature', () => {
  const contactData = ref({})
  const currentSignatureIndex = ref(0)
  const intervals = ref([])
  const socialFields = ['website', 'facebook', 'instagram', 'youtube', 'linkedin', 'x', 'chat']
  const textFields = ['disclaimer', 'displayName', 'company', 'email', 'jobTitle', 'lastName', 'mobileNumber', 'profession', 'telephoneNumber']
  const imageFields = ['imageUrl', 'logoUrl']
  const themeFields = ['theme', 'font', 'fontSize']
  const allFields = [...textFields, ...socialFields, ...imageFields, ...themeFields]
  const availableFields = ref(allFields)
  const isUploading = ref(false)
  const currentUpload = ref(null)
  const iconCache = ref({ white: {}, black: {} })
  // avoid concurrent icon uploads by storing in-progress upload promises,
  // ensuring each icon variant is uploaded only once
  const iconInflight = new Map()

  const whiteFontThemes = [
    { id: 'black', background: '#000000', text: 'white' },
    { id: 'red', background: '#D92C41', text: 'white' },
    { id: 'blue', background: '#2D6CDF', text: 'white' },
    { id: 'green', background: '#267253', text: 'white' }
  ]

  const darkFontThemes = [
    { id: 'pink', background: '#FFCCDB', text: 'black' },
    { id: 'yellow', background: '#FFF5CC', text: 'black' },
    { id: 'lightgreen', background: '#DDF7D4', text: 'black' },
    { id: 'lightblue', background: '#CEEEFE', text: 'black' },
    { id: 'purple', background: '#CCD8FF', text: 'black' },
    { id: 'grey', background: '#E6E5E5', text: 'black' }
  ]

  function getIconPath (field, variant = 'black') {
    const iconMap = {
      instagram: './themes/default/icons/socials/instagram',
      facebook: './themes/default/icons/socials/facebook',
      linkedin: './themes/default/icons/socials/linkedin',
      youtube: './themes/default/icons/socials/youtube',
      x: './themes/default/icons/socials/twitter-x',
      chat: './themes/default/icons/socials/chat-left-dots',
      website: './themes/default/icons/socials/globe',
      email: './themes/default/icons/socials/envelope',
      mobileNumber: './themes/default/icons/socials/phone',
      telephoneNumber: './themes/default/icons/socials/telephone'
    }
    const suffix = variant ? `_${variant}` : ''
    const path = `${iconMap[field]}${suffix}.png`

    return iconMap[field] ? path : ''
  }

  async function ensureIconUrl (field) {
    const variant = availableFields.value.includes('theme')
      ? contactData.value.theme?.text
      : 'black'
    const cached = iconCache.value?.[variant]?.[field]
    if (cached) return cached

    const key = `${variant}:${field}`
    if (iconInflight.has(key)) return iconInflight.get(key)

    const promise = (async () => {
      const path = getIconPath(field, variant)
      if (!path) return

      const fetchResult = await fetch(path)
      if (!fetchResult.ok) throw new Error(`Failed to load image from ${path}`)
      const blob = await fetchResult.blob()

      const fileName = path.split('/').pop()
      const file = new File([blob], fileName, { type: blob.type })

      const url = await uploadImage({ file, field: `${field}IconUrl` })
      if (url) iconCache.value[variant][field] = url
      return url
    })().finally(() => {
      iconInflight.delete(key)
    })

    iconInflight.set(key, promise)
    return promise
  }

  async function applyIconsForTheme () {
    const variant = contactData.value.theme?.text
    const fields = [...socialFields, ...textFields]

    for (const field of fields) {
      if (!availableFields.value?.includes(field)) continue
      if (!contactData.value[field]) continue

      const url = await ensureIconUrl(field, variant)
      if (!url) continue

      contactData.value[`${field}IconUrl`] = url
    }
  }

  watch(
    () => [contactData.value.theme?.text, availableFields.value],
    async (newText, oldText) => {
      if (!newText || newText === oldText) return
      await applyIconsForTheme()
    }
  )

  const defaultContactData = {
    address: '123 Base Road, Lunar Way, Moon 1234, The Moon',
    displayName: gt('Name Surname'),
    company: 'Example Company',
    email: 'yn@example.com',
    jobTitle: gt('Job title'),
    lastName: '',
    profession: gt('Job title'),
    telephoneNumber: '+00 30 12345678',
    mobileNumber: '+00 151 23456789',
    theme: whiteFontThemes.find(theme => theme.id === 'black'),
    disclaimer: gt('This email may contain confidential information.'),
    imageUrl: '',
    logoUrl: '',
    website: 'www.example.com',
    facebook: 'username',
    instagram: 'username',
    youtube: '@username',
    linkedin: 'username',
    x: 'username',
    chat: 'username'
  }

  const fontFormats = getFontFormats().split(';')
    .filter(str => !/^(Web|Wing)dings/.test(str))
    .map(pair => {
      pair = pair.split('=')
      return { label: pair[0], value: pair[1] }
    })

  function resetContactData () {
    getCurrentUser().then((model) => {
      const recentContactData = mailSettings.get('signatures/recentTemplateData', {})
      const attributes = model.attributes
      const address = postalAddress.format(attributes, 'business') || postalAddress.format(attributes, 'home') || postalAddress.format(attributes, 'other')
      contactData.value = {
        address: address.replace(/\n/g, ', '),
        displayName: model.get('display_name', ''),
        company: model.get('company', ''),
        email: model.get('email1') || model.get('email2') || model.get('email3') || '',
        jobTitle: model.get('jobTitle', ''),
        lastName: model.get('last_name', ''),
        profession: model.get('profession', ''),
        telephoneNumber: model.get('telephone_business1') || model.get('telephone_business2') || model.get('telephone_business3') || '',
        mobileNumber: model.get('cellular_telephone1') || model.get('cellular_telephone2') || model.get('cellular_telephone3') || '',
        theme: whiteFontThemes.find(theme => theme.id === 'black'),
        font: fontFormats.find(format => format.label === 'System').value,
        fontSize: 'normal',
        ...recentContactData
      }
      iconCache.value = { white: {}, black: {} }
    })
  }

  function createSignature ({ content, displayname, insertion = 'below' }) {
    const contactDataWithoutUrlFields = Object.fromEntries(
      Object.entries(contactData.value).filter(([key]) => !key.endsWith('Url'))
    )
    const theme = toRaw(contactData.value.theme)
    const templateData = { ...contactDataWithoutUrlFields, theme }
    mailSettings.set('signatures/recentTemplateData', templateData).save()

    const snippet = {
      id: null,
      displayname,
      content,
      type: 'signature',
      module: 'io.ox/mail',
      misc: {
        insertion,
        'content-type': 'text/html'
      },
    }

    return snippetsAPI.create(snippet)
  }

  function addKeepalive (id) {
    const timeout = Math.round(coreSettings.get('maxUploadIdleTimeout', 200000) * 0.9)
    intervals.value.push(setInterval(mailAPI.keepalive, timeout, id))
  }

  function clearKeepalive () { intervals.value.forEach(clearInterval) }

  async function uploadImage ({ file, field = 'imageUrl' }) {
    // image broken, don't upload it and show error message
    if (file.size === 0 || file.type === '' || !file.name) {
      return Promise.reject(new Error(gt('One of the embedded images could not be used.')))
    }

    const formData = new FormData()
    // avoid the generic blob filename
    if (!file.name) {
      const type = file.type.replace('image/', '') || 'png'
      formData.append('file', file, 'image.' + type)
    } else {
      formData.append('file', file)
    }

    currentUpload.value = http.UPLOAD({
      module: 'file',
      params: { action: 'new', module: 'mail', type: 'image' },
      data: formData,
      fixPost: true
    })

    return currentUpload.value.then(response => {
      const url = ox.apiRoot + '/file'
      const urlParams = new URLSearchParams({
        action: 'get',
        id: response.data[0],
        session: ox.session
      }).toString()

      addKeepalive(response.data[0])
      currentUpload.value = null

      return url + '?' + urlParams
    }).catch(response => {
      if (response.error !== 'abort') throw response
    })
  }

  async function ensureIconForField (field) {
    if (!contactData.value[field]) return

    const iconField = `${field}IconUrl`
    if (contactData.value[iconField]) return

    const variant = contactData.value.theme?.text
    const url = await ensureIconUrl(field, variant)
    if (url) contactData.value[iconField] = url
  }

  return {
    contactData,
    currentSignatureIndex,
    resetContactData,
    availableFields,
    createSignature,
    uploadImage,
    clearKeepalive,
    socialFields,
    textFields,
    imageFields,
    themeFields,
    allFields,
    whiteFontThemes,
    darkFontThemes,
    defaultContactData,
    isUploading,
    currentUpload,
    ensureIconForField,
    getIconPath,
    fontFormats
  }
})
